import { useFormState, useWatch } from "react-hook-form";
import PropTypes from "prop-types";
import { useParams } from "react-router";

import Options from "shared/components/inputs/form-fields/Options";
import VStack from "shared/components/layout/stack/VStack";
import Form from "shared/components/inputs/form/Form";
import TextInput from "shared/components/inputs/form-fields/TextInput";
import ModalActions from "shared/components/feedback/modals/ModalActions";

import { sendNotification } from "shared/components/feedback/notification/notification";
import FormFeedbackNotificationText from "shared/components/feedback/error/FormFeedbackNotificationText";

import useClientLOBs from "features/admin/clients/single/useClientLOBs";

const attributesOptions = [{ label: "Active", value: "Active" }];

const ModalCTA = ({ isEditing, isLoading, onClose, handleOnClickDeleteLOB }) => {
    const { isValid } = useFormState();
    const formData = useWatch();

    return (
        <ModalActions
            primaryAction={{
                type: "submit",
                label: isEditing ? "Save" : "Add",
                disabled: !isValid,
            }}
            secondaryAction={{
                type: "button",
                label: "Cancel",
                onClick: onClose,
            }}
            {...(isEditing && {
                tertiaryAction: {
                    type: "button",
                    leadingIcon: "delete",
                    label: "Delete",
                    onClick: () => handleOnClickDeleteLOB(formData),
                },
            })}
            as="fieldset"
            disabled={isLoading}
        />
    );
};

function transformLOBDataToFormData(clientLOBData = {}) {
    return {
        name: clientLOBData.GroupName,
        attributes: attributesOptions
            .map((attribute) => attribute.value)
            .filter((item) =>
                item === "Active"
                    ? clientLOBData[item] ?? true
                    : clientLOBData[item]
            )
            .join(","),
    };
}

function transformFormDataToLOBData(
    formData = {},
    LOBData = {},
    clientId = null,
    ClientLOBId = undefined
) {
    const newLOBData = { ...LOBData };
    newLOBData.Id = ClientLOBId;

    newLOBData.Name = formData.name;

    attributesOptions.forEach((attribute) => {
        newLOBData[attribute.value] = formData.attributes
            .split(",")
            .includes(attribute.value);
    });

    newLOBData.ParentId = clientId;

    newLOBData.ClientId = clientId;

    return newLOBData;
}

const ClientGroupsLOBForm = ({ clientLOBIdToEdit, onClose }) => {
    const { clientId } = useParams();

    const { clientLOBs, createClientLOB, updateClientLOB, deleteClientLOB } = useClientLOBs();

    if (clientLOBs.isLoading) {
        return <div>Loading...</div>;
    }

    if (clientLOBs.error) {
        return <div>Error</div>;
    }

    const isEditing = clientLOBIdToEdit !== undefined;
    const modifier = isEditing ? updateClientLOB : createClientLOB;

    const currentClientLOBData = isEditing
        ? clientLOBs?.data.find(
              (clientLOB) => clientLOB.clientGroupID === clientLOBIdToEdit
          )
        : "";

    const clientLOBsDataExceptCurrent = clientLOBs?.data.filter(
        (clientLOB) => clientLOB.clientGroupID !== clientLOBIdToEdit
    );

    if (isEditing && !currentClientLOBData) {
        return <div>Could not find line of business</div>;
    }

    const handleOnClickDeleteLOB = async (prevData) => {
        // keeping active true for undo
        const restoreData = {
            ...prevData,
            attributes: attributesOptions[0].value,
        };

        const response = await deleteClientLOB.mutateAsync({ clientLOBId: clientLOBIdToEdit });
        onClose();
        sendNotification(
            <FormFeedbackNotificationText
                responseStatus={response?.status}
                isDeleting={true}
                name={currentClientLOBData.GroupName}
            />,
            {
                close: true,
                action: true,
                actionProps: {
                    label: "Undo",
                    onClick: () => onSubmit(restoreData),
                },
            }
        );
    };

    const onSubmit = async (formData) => {
        const clientLOBsData = transformFormDataToLOBData(
            formData,
            currentClientLOBData,
            clientId,
            clientLOBIdToEdit
        );

        const allData = {
            clientLOBId: clientLOBIdToEdit,
            body: clientLOBsData,
        };

        const response = await modifier.mutateAsync(allData);
        onClose();
        sendNotification(
            <FormFeedbackNotificationText
                responseStatus={response?.status}
                isEditing={isEditing}
                name={
                    isEditing
                        ? currentClientLOBData.GroupName
                        : clientLOBsData.Name
                }
            />
        );
    };

    return (
        <Form
            onSubmit={onSubmit}
            defaultValues={transformLOBDataToFormData(currentClientLOBData)}
            mode="onChange"
        >
            <VStack spacing="Three">
                <VStack spacing="Two">
                    <TextInput
                        type="text"
                        name="name"
                        label="Name"
                        placeholder="LOB Name"
                        validations={{
                            required: true,
                            validate: (value) =>
                                clientLOBsDataExceptCurrent?.filter(
                                    (clientLOB) =>
                                        clientLOB?.GroupName?.toLowerCase() ===
                                        value.toLowerCase()
                                ).length === 0 || "Name is already exist",
                        }}
                    />
                    <Options name="attributes" options={attributesOptions} />
                </VStack>
                <ModalCTA
                    isEditing={isEditing}
                    handleOnClickDeleteLOB={handleOnClickDeleteLOB}
                    onClose={onClose}
                    isLoading={modifier.isLoading}
                />
            </VStack>
        </Form>
    );
};

ModalCTA.propTypes = {
    isEditing: PropTypes.bool,
    isLoading: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    handleOnClickDeleteLOB: PropTypes.func
};

ClientGroupsLOBForm.propTypes = {
    clientLOBIdToEdit: PropTypes.number,
    onClose: PropTypes.func.isRequired
};

export default ClientGroupsLOBForm;
